import {
  getOverlappingPaymentMergeCandidates,
  mergeOverlappingPayment,
} from "common/services/payments.service";
import * as moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Modal, ModalBody, ModalHeader, Spinner } from "reactstrap";
import { resolveErrorMessage } from "./resolution";

type Side = "payment" | "overlappingPayment";
const SIDES: Side[] = ["payment", "overlappingPayment"];

type FieldMeta = {
  label: string;
  /** Text shown for a value; empty string means "not provided". */
  format: (value: any) => string;
  /** What makes two values the same; defaults to the formatted text. */
  identity?: (value: any) => unknown;
};

const named = (value: any) => value?.name ?? "";
const byId = (value: any) => value?.id ?? null;
const date = (value: any) =>
  value ? moment(new Date(value)).format("DD MMM Y") : "";
const amount = (value: any) =>
  value === null || value === undefined || value === ""
    ? ""
    : `$${Number(value).toLocaleString()}`;

/**
 * Labels and formatting for the fields the server asks about. The server
 * decides which fields are merged (it sends the list); anything it sends that
 * is missing here still renders, under its raw name.
 */
const FIELD_META: Record<string, FieldMeta> = {
  individual: {
    label: "Individual",
    format: (v) => v?.fullName ?? "",
    identity: byId,
  },
  mda: {
    label: "MDA & Region",
    format: (v) =>
      v ? [v.name, v.region?.name].filter(Boolean).join(" - ") : "",
    identity: byId,
  },
  agency: { label: "Agency", format: named, identity: byId },
  positionTitle: { label: "Position Title", format: (v) => v ?? "" },
  positionType: { label: "Position Type", format: named, identity: byId },
  paymentCategory: { label: "Payment Category", format: named, identity: byId },
  staffId: { label: "Staff ID", format: (v) => v ?? "" },
  amountPerMonth: {
    label: "Amount / Month",
    format: amount,
    identity: (v) => (v === null || v === undefined ? null : Number(v)),
  },
  startDate: { label: "Start Date", format: date },
  endDate: { label: "End Date", format: date },
  projectOrFund: { label: "Project or Fund", format: (v) => v ?? "" },
  donorOrFund: { label: "Donor / Fund", format: (v) => v ?? "" },
  isSalaryAligned: {
    label: "Aligned with Harmonized Salary",
    format: (v) => (v === true ? "Yes" : v === false ? "No" : ""),
  },
  thematicArea: { label: "Thematic Area", format: named, identity: byId },
  sector: { label: "Sector", format: named, identity: byId },
  dataProvidedBy: { label: "Data Provided By", format: (v) => v ?? "" },
  additionalComments: { label: "Additional Comments", format: (v) => v ?? "" },
};

const metaFor = (field: string): FieldMeta =>
  FIELD_META[field] ?? {
    label: field,
    format: (v) => (v === null || v === undefined ? "" : String(v)),
  };

const identityOf = (field: string, value: any) => {
  const meta = metaFor(field);
  return meta.identity ? meta.identity(value) : meta.format(value);
};

/**
 * Second step of resolving a pair as a double entry: the reviewer picks, field
 * by field, which payment's value the merged payment keeps. Fields where both
 * payments already agree are settled automatically; every other field needs
 * an explicit choice before the merge is allowed.
 */
const MergePaymentsModal: React.FC<{
  isOpen: boolean;
  recordId?: string | number;
  comments?: string;
  /** Return to the resolve form, keeping what was entered there. */
  onBack: () => void;
  /** Close the whole flow without merging. */
  onCancel: () => void;
  onMerged: () => void;
}> = ({ isOpen, recordId, comments, onBack, onCancel, onMerged }) => {
  const [candidates, setCandidates] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [selections, setSelections] = useState<Record<string, Side>>({});
  const [showMissing, setShowMissing] = useState(false);
  const [merging, setMerging] = useState(false);

  const fields: string[] = useMemo(
    () => candidates?.fields ?? [],
    [candidates]
  );

  const matching = useMemo(() => {
    const same = new Set<string>();
    if (!candidates) return same;
    for (const field of fields) {
      if (
        identityOf(field, candidates.payment?.[field]) ===
        identityOf(field, candidates.overlappingPayment?.[field])
      )
        same.add(field);
    }
    return same;
  }, [candidates, fields]);

  const load = useCallback(async () => {
    if (!recordId) return;
    setLoading(true);
    setLoadError("");
    setCandidates(null);
    setSelections({});
    setShowMissing(false);
    try {
      const response: any = await getOverlappingPaymentMergeCandidates(
        recordId
      );
      setCandidates(response?.data ?? null);
    } catch (e: any) {
      setLoadError(
        resolveErrorMessage(e, "Could not load the payments to merge")
      );
    } finally {
      setLoading(false);
    }
  }, [recordId]);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  // Matching fields have nothing to decide, so they count as chosen.
  const effectiveSelections = useMemo(() => {
    const all: Record<string, Side> = { ...selections };
    matching.forEach((field) => {
      if (!all[field]) all[field] = "payment";
    });
    return all;
  }, [selections, matching]);

  const missing = fields.filter((field) => !effectiveSelections[field]);
  const differing = fields.filter((field) => !matching.has(field));
  const chosenCount = differing.length - missing.length;
  const allChosen = missing.length === 0;

  const choose = (field: string, side: Side) =>
    setSelections((current) => ({ ...current, [field]: side }));

  const chooseAll = (side: Side) =>
    setSelections((current) => {
      const next = { ...current };
      differing.forEach((field) => (next[field] = side));
      return next;
    });

  const handleMerge = async () => {
    if (missing.length > 0) {
      setShowMissing(true);
      document
        .getElementById(`merge-${missing[0]}-payment`)
        ?.focus({ preventScroll: false });
      return;
    }
    if (!recordId) return;

    setMerging(true);
    try {
      await mergeOverlappingPayment(recordId, {
        selections: effectiveSelections,
        comments,
      });
      toast.success("Payments merged into one");
      onMerged();
    } catch (e: any) {
      toast.error(resolveErrorMessage(e, "Merging the payments failed"));
    } finally {
      setMerging(false);
    }
  };

  const olderSide: Side | null = candidates
    ? candidates.payment?.id < candidates.overlappingPayment?.id
      ? "payment"
      : "overlappingPayment"
    : null;
  const deletedId = olderSide
    ? candidates?.[olderSide === "payment" ? "overlappingPayment" : "payment"]
        ?.id
    : null;
  const personName =
    candidates?.payment?.individual?.fullName ||
    candidates?.overlappingPayment?.individual?.fullName;

  const renderValue = (field: string, value: any) => {
    const text = metaFor(field).format(value);
    return text ? (
      <span className="merge-choice-value">{text}</span>
    ) : (
      <span className="merge-choice-value merge-choice-empty">
        Not provided
      </span>
    );
  };

  return (
    <Modal isOpen={isOpen} toggle={onCancel} size="xl" centered scrollable>
      <ModalHeader className="bg-light p-3" toggle={onCancel} tag="div">
        <div className="d-flex align-items-center gap-3">
          <div className="avatar-sm flex-shrink-0">
            <span className="avatar-title rounded bg-primary-subtle text-primary fs-20">
              <i className="ri-git-merge-line" aria-hidden="true" />
            </span>
          </div>
          <div>
            <h5 className="modal-title mb-1">Merge Double Entry</h5>
            {candidates && (
              <p className="text-muted mb-0 fs-13">
                Payment #{candidates.payment?.id} and #
                {candidates.overlappingPayment?.id}
                {personName ? ` · ${personName}` : ""}
              </p>
            )}
          </div>
        </div>
      </ModalHeader>

      <ModalBody className="merge-body">
        {loading && (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="text-muted mt-2 mb-0">Loading both payments…</p>
          </div>
        )}

        {!loading && loadError && (
          <div className="alert alert-danger mb-0" role="alert">
            {loadError}
          </div>
        )}

        {!loading && candidates && (
          <>
            {/* Column headings stay in view while scrolling the fields. */}
            <div className="merge-grid merge-grid-head">
              <div className="merge-head-hint">
                Pick the value to keep in each row.
              </div>
              {SIDES.map((side) => {
                const kept = side === olderSide;
                const record = candidates[side];
                return (
                  <div
                    key={side}
                    className={`merge-record ${
                      kept ? "merge-record-kept" : "merge-record-deleted"
                    }`}
                  >
                    <div className="d-flex align-items-start justify-content-between flex-wrap gap-1">
                      <div>
                        <div className="fw-semibold">Payment #{record?.id}</div>
                        <div className="text-muted fs-12">
                          {record?.createdAt
                            ? `Added ${moment(
                                new Date(record.createdAt)
                              ).format("DD MMM Y")}`
                            : "Date added unknown"}
                        </div>
                      </div>
                      <span
                        className={`badge flex-shrink-0 ${
                          kept
                            ? "bg-primary-subtle text-primary"
                            : "bg-danger-subtle text-danger"
                        }`}
                      >
                        {kept ? "Kept" : "Deleted after merge"}
                      </span>
                    </div>
                    {differing.length > 0 && (
                      <button
                        type="button"
                        className="btn btn-sm btn-soft-primary w-100 mt-2"
                        onClick={() => chooseAll(side)}
                      >
                        <i
                          className="ri-check-double-line align-bottom me-1"
                          aria-hidden="true"
                        />
                        Use all from #{record?.id}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {showMissing && !allChosen && (
              <div
                className="alert alert-danger d-flex gap-2 mt-3 mb-0"
                role="alert"
              >
                <i className="ri-error-warning-line fs-16" aria-hidden="true" />
                <span>
                  Choose a value for the {missing.length} highlighted field
                  {missing.length === 1 ? "" : "s"} before merging.
                </span>
              </div>
            )}

            {differing.length === 0 && (
              <div className="merge-empty">
                <i
                  className="ri-checkbox-circle-line text-success fs-24"
                  aria-hidden="true"
                />
                <p className="mb-0">
                  These payments match on every field. Merging simply removes
                  payment #{deletedId}.
                </p>
              </div>
            )}

            {/* Every field, in one list and one order, so the reviewer sees the
                whole record being compared. Only differing fields ask for a
                choice; matching ones are shown once across both columns. */}
            <section className="merge-section">
              <div className="merge-section-title">
                <span>Fields</span>
                {differing.length > 0 && (
                  <span className="badge bg-primary-subtle text-primary">
                    {differing.length} to choose
                  </span>
                )}
                {matching.size > 0 && (
                  <span className="badge bg-success-subtle text-success">
                    {matching.size} same in both
                  </span>
                )}
              </div>

              {fields.map((field) => {
                const labelId = `merge-${field}-label`;

                if (matching.has(field)) {
                  return (
                    <div key={field} className="merge-grid merge-row-matching">
                      <div id={labelId} className="merge-grid-label">
                        {metaFor(field).label}
                      </div>
                      <div
                        className="merge-matching-value"
                        aria-describedby={labelId}
                      >
                        <i
                          className="ri-check-line text-success"
                          aria-hidden="true"
                        />
                        {renderValue(field, candidates.payment?.[field])}
                        <span className="badge bg-success-subtle text-success ms-auto">
                          Same in both
                        </span>
                      </div>
                    </div>
                  );
                }

                const isMissing = showMissing && !effectiveSelections[field];
                const chosen = !!selections[field];
                return (
                  <div
                    key={field}
                    role="radiogroup"
                    aria-labelledby={labelId}
                    aria-invalid={isMissing || undefined}
                    className={`merge-grid merge-row${
                      chosen ? " is-chosen" : ""
                    }${isMissing ? " is-missing" : ""}`}
                  >
                    <div id={labelId} className="merge-grid-label">
                      {metaFor(field).label}
                      {isMissing && (
                        <span className="merge-row-error">Choose one</span>
                      )}
                    </div>
                    {SIDES.map((side) => {
                      const id = `merge-${field}-${side}`;
                      return (
                        <div key={side} className="merge-cell">
                          <input
                            type="radio"
                            className="merge-choice-input"
                            id={id}
                            name={`merge-${field}`}
                            value={side}
                            checked={selections[field] === side}
                            onChange={() => choose(field, side)}
                          />
                          <label htmlFor={id} className="merge-choice">
                            <span
                              className="merge-choice-radio"
                              aria-hidden="true"
                            />
                            {renderValue(field, candidates[side]?.[field])}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </section>
          </>
        )}
      </ModalBody>

      <div className="modal-footer merge-footer">
        <div className="merge-progress" aria-live="polite">
          {candidates && differing.length > 0 && (
            <>
              <div className="d-flex align-items-center gap-2 fs-13">
                {allChosen ? (
                  <span className="text-success fw-medium">
                    <i className="ri-checkbox-circle-fill align-bottom me-1" />
                    All differences chosen
                  </span>
                ) : (
                  <span className="text-muted">
                    {chosenCount} of {differing.length} differences chosen
                  </span>
                )}
              </div>
              <div
                className="progress progress-sm mt-1"
                role="progressbar"
                aria-label="Differences chosen"
                aria-valuemin={0}
                aria-valuemax={differing.length}
                aria-valuenow={chosenCount}
              >
                <div
                  className={`progress-bar ${
                    allChosen ? "bg-success" : "bg-primary"
                  }`}
                  style={{
                    width: `${(chosenCount / differing.length) * 100}%`,
                  }}
                />
              </div>
            </>
          )}
        </div>
        <div className="hstack gap-2 flex-wrap justify-content-end">
          <button type="button" className="btn btn-light" onClick={onBack}>
            <i className="ri-arrow-left-line align-bottom me-1" />
            Back
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleMerge}
            disabled={!candidates || merging}
          >
            {merging ? (
              <Spinner
                color="light"
                size="sm"
                style={{ marginRight: "8px", marginBottom: "-1px" }}
              />
            ) : (
              <i className="ri-git-merge-line align-bottom me-1" />
            )}
            Merge Payments
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default MergePaymentsModal;
